import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import {
    Button,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import OrgApiService from '../../../api/OrgApiService';
import LocationApiService from '../../../api/LocationApiService';
import FormControlLabel from '@mui/material/FormControlLabel';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AuditChecklistItemApiService from '../../../api/AuditChecklistItemApiService';
import AuditChecklistApiService from '../../../api/AuditChecklistApiService';
import CategoryApiService from '../../../api/CategoryApiService';
import PriorityApiService from '../../../api/PriorityApiService';
import StatusApiService from '../../../api/StatusApiService';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import ConfirmDeleteDialogHazard from '../ConfirmDeleteHazard';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AuthUtil from '../../Utils/AuthUtil';
import SaveButton from '../../Common/SaveButton';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';
import { useSnackbar } from '../../Common/SnackbarProvider';

const orgApiService = new OrgApiService();
const locationApiService = new LocationApiService();
const auditChecklistItemApiService = new AuditChecklistItemApiService();
const auditChecklistApiService = new AuditChecklistApiService();
const categoryApiService = new CategoryApiService();
const priorityApiService = new PriorityApiService();
const statusApiService = new StatusApiService();

const emptyLocation = {
    projectId: 0,
    projectName: '\u00A0'
}

const emptyOrganization = { ogId: 0, ogName: '\u00A0' }

const buildNoneOption = (t) => ({ value: null, label: t('common.none') });

const gridSx = {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#fafafa',
        borderBottom: '2px solid #e0e0e0',
        fontSize: '0.8rem',
        textTransform: 'uppercase',
        color: '#666',
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: '#f5f5f5',
    },
    '& .MuiDataGrid-cell': {
        fontSize: '0.875rem',
    },
};

export default function EditAuditChecklistModal (props) {
    const {
        open,
        currentAuditChecklist,
        onCancel,
        onSave,
    } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const NONE_OPTION = useMemo(() => buildNoneOption(t), [t]);
    const [title, setTitle] = useState(t('editAuditChecklist.title.create'));
    const [loading, setLoading] = useState(false);
    const [auditChecklistTitle, setAuditChecklistTitle] = useState('');
    const [organizations, setOrganizations] = useState([emptyOrganization]);
    const [selectedOrganization, setSelectedOrganization] = useState(emptyOrganization);
    const [locations, setLocations] = useState([emptyLocation]);
    const [selectedLocation, setSelectedLocation] = useState(emptyLocation);
    const [active, setActive] = useState(false);
    const [auditChecklistItems, setAuditChecklistItems] = useState([]);
    const [currentSelectedAuditChecklistItem, setCurrentSelectedAuditChecklistItem] = useState();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const [categories, setCategories] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [statuses, setStatuses] = useState([]);

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);

    const categoryOptions = useMemo(() => [NONE_OPTION, ...categories.map(c => ({ value: c.ctId, label: c.localizedName ?? c.ctName }))], [categories, NONE_OPTION]);
    const priorityOptions = useMemo(() => [NONE_OPTION, ...priorities.map(p => ({ value: p.prId, label: p.localizedName ?? p.prName }))], [priorities, NONE_OPTION]);
    const statusOptions = useMemo(() => [NONE_OPTION, ...statuses.map(s => ({ value: s.stId, label: s.localizedName ?? s.stName }))], [statuses, NONE_OPTION]);

    const columns = [
        {
            field: 'ciPrompt',
            headerName: t('editAuditChecklist.column.prompt'),
            flex: 1.2,
            editable: true,
        },
        {
            field: 'ciCategoryId',
            headerName: t('fieldName.category'),
            flex: 0.55,
            editable: true,
            type: 'singleSelect',
            valueOptions: categoryOptions,
            valueFormatter: (params) => {
                const match = categories.find(c => c.ctId === params.value);
                return match ? (match.localizedName ?? match.ctName) : '';
            },
        },
        {
            field: 'ciPriorityId',
            headerName: t('fieldName.priority'),
            flex: 0.55,
            editable: true,
            type: 'singleSelect',
            valueOptions: priorityOptions,
            valueFormatter: (params) => {
                const match = priorities.find(p => p.prId === params.value);
                return match ? (match.localizedName ?? match.prName) : '';
            },
        },
        {
            field: 'ciStatusId',
            headerName: t('fieldName.status'),
            flex: 0.55,
            editable: true,
            type: 'singleSelect',
            valueOptions: statusOptions,
            valueFormatter: (params) => {
                const match = statuses.find(s => s.stId === params.value);
                return match ? (match.localizedName ?? match.stName) : '';
            },
        },
        {
            field: 'ciCreateTask',
            headerName: t('editAuditChecklist.column.autoTask'),
            flex: 0.35,
            editable: true,
            type: 'boolean',
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'childItems',
            headerName: t('editAuditChecklist.column.subQuestions'),
            flex: 0.3,
            sortable: false,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const count = params.value?.length || 0;
                return count > 0 ? (
                    <Chip label={count} size="small" variant="outlined" color="primary" />
                ) : null;
            },
        },
        {
            field: 'delete',
            headerName: '',
            flex: 0.2,
            filterable: false,
            sortable: false,
            align: 'center',
            renderCell: (params) => (
                <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                        setCurrentSelectedAuditChecklistItem(params.row);
                        setShowDeleteDialog(true);
                    }}
                >
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            ),
        },
    ];

    const subQuestionColumns = [
        {
            field: 'ciPrompt',
            headerName: t('editAuditChecklist.column.subQuestionPrompt'),
            flex: 1.2,
            editable: true,
        },
        {
            field: 'ciTriggerAnswer',
            headerName: t('editAuditChecklist.column.trigger'),
            flex: 0.45,
            editable: true,
            type: 'singleSelect',
            valueOptions: [
                { value: '1', label: t('common.no') },
                { value: '0', label: t('common.yes') },
            ],
            renderCell: (params) => (
                <Chip
                    label={params.value === '0' ? t('common.yes') : t('common.no')}
                    size="small"
                    variant="outlined"
                    color={params.value === '0' ? 'success' : 'warning'}
                />
            ),
        },
        {
            field: 'ciCategoryId',
            headerName: t('fieldName.category'),
            flex: 0.5,
            editable: true,
            type: 'singleSelect',
            valueOptions: categoryOptions,
            valueFormatter: (params) => {
                const match = categories.find(c => c.ctId === params.value);
                return match ? (match.localizedName ?? match.ctName) : '';
            },
        },
        {
            field: 'ciPriorityId',
            headerName: t('fieldName.priority'),
            flex: 0.5,
            editable: true,
            type: 'singleSelect',
            valueOptions: priorityOptions,
            valueFormatter: (params) => {
                const match = priorities.find(p => p.prId === params.value);
                return match ? (match.localizedName ?? match.prName) : '';
            },
        },
        {
            field: 'ciStatusId',
            headerName: t('fieldName.status'),
            flex: 0.5,
            editable: true,
            type: 'singleSelect',
            valueOptions: statusOptions,
            valueFormatter: (params) => {
                const match = statuses.find(s => s.stId === params.value);
                return match ? (match.localizedName ?? match.stName) : '';
            },
        },
        {
            field: 'ciCreateTask',
            headerName: t('editAuditChecklist.column.autoTask'),
            flex: 0.35,
            editable: true,
            type: 'boolean',
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'delete',
            headerName: '',
            flex: 0.2,
            filterable: false,
            sortable: false,
            align: 'center',
            renderCell: (params) => (
                <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                        setCurrentSelectedAuditChecklistItem(params.row);
                        setShowDeleteDialog(true);
                    }}
                >
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            ),
        },
    ];

    useEffect(() => {
        if (open) {
            if (currentAuditChecklist) {
                setTitle(t('editAuditChecklist.title.edit'));
            }
            else {
                setTitle(t('editAuditChecklist.title.create'));
            }

            setAuditChecklistTitle(currentAuditChecklist?.acTitle || '');
            setActive(currentAuditChecklist?.acIsActive || false);
            setSelectedOrganization(emptyOrganization);
            setSelectedLocation(emptyLocation);
            setAuditChecklistItems([]);

            Promise.all([
                categoryApiService.GetCategories(),
                priorityApiService.GetPriorities(),
                statusApiService.getStatuses(),
            ]).then(([catResponse, priResponse, statResponse]) => {
                setCategories(catResponse.data || []);
                setPriorities(priResponse.data || []);
                setStatuses(statResponse.data || []);
            });

            if (isSuperAdmin) {
                orgApiService.getOrgs().then(orgResponse => {
                    let organizationList = [emptyOrganization];
                    organizationList = organizationList.concat(orgResponse.data);

                    setOrganizations(organizationList);

                    setSelectedOrganization(currentAuditChecklist ? {
                        ogId: currentAuditChecklist.organizationId,
                        ogName: currentAuditChecklist.organizationName
                    } : emptyOrganization);
                });
            }

            if (currentAuditChecklist) {
                updateLocationsForOrganization(currentAuditChecklist.organizationId).then(() => {
                    setSelectedLocation({ projectId: currentAuditChecklist.acProject,
                        projectName: currentAuditChecklist.projectName });
                })

                setLoading(true);
                auditChecklistItemApiService.getAuditChecklistItems(currentAuditChecklist.acId).then(auditChecklistItemResponse => {
                    const items = auditChecklistItemResponse.data
                        .map(item => ({
                            ...item,
                            childItems: (item.childItems || [])
                                .map(child => ({
                                    ...child,
                                    ciCreateTask: child.ciCreateTask ?? true,
                                    ciTriggerAnswer: child.ciTriggerAnswer || '1',
                                }))
                                .sort((a, b) => a.ciSortSeq - b.ciSortSeq),
                            ciCreateTask: item.ciCreateTask ?? true,
                        }))
                        .sort((a, b) => a.ciSortSeq - b.ciSortSeq);
                    setAuditChecklistItems(items);
                    setLoading(false);
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, currentAuditChecklist]);

    const dialogTitle = () => {
        return t('common.confirmDelete');
    };

    const dialogLabel = () => {
        return t('common.confirmDeleteWithName', { name: currentSelectedAuditChecklistItem?.ciPrompt ?? '' });
    };

    async function onDeleteAuditChecklistItem (deleteValue) {
        if (deleteValue) {
            const itemToDelete = currentSelectedAuditChecklistItem;

            if (itemToDelete.ciParentItemId) {
                setAuditChecklistItems(prev => prev.map(parent => {
                    if (parent.childItems?.some(c => c.ciId === itemToDelete.ciId)) {
                        return {
                            ...parent,
                            childItems: parent.childItems.filter(c => c.ciId !== itemToDelete.ciId),
                        };
                    }
                    return parent;
                }));
            } else {
                setAuditChecklistItems(prev => prev.filter(item => item.ciId !== itemToDelete.ciId));
            }

            snackbar.success(t('editAuditChecklist.toast.itemDeleted'));

            setShowDeleteDialog(false);
            setCurrentSelectedAuditChecklistItem(null);
        }
    }

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        let fullDto = getUpdatedAuditChecklist();
        fullDto.auditChecklistItems = auditChecklistItems.map(item => ({
            ciId: item.newItem ? 0 : item.ciId,
            ciPrompt: item.ciPrompt,
            ciSortSeq: item.ciSortSeq,
            ciCategoryId: item.ciCategoryId,
            ciPriorityId: item.ciPriorityId,
            ciStatusId: item.ciStatusId,
            ciCreateTask: item.ciCreateTask,
            ciParentItemId: null,
            childItems: (item.childItems || []).map(child => ({
                ciId: child.newItem ? 0 : child.ciId,
                ciPrompt: child.ciPrompt,
                ciSortSeq: child.ciSortSeq,
                ciCategoryId: child.ciCategoryId,
                ciPriorityId: child.ciPriorityId,
                ciStatusId: child.ciStatusId,
                ciCreateTask: child.ciCreateTask,
                ciTriggerAnswer: child.ciTriggerAnswer,
            })),
        }));

        let auditChecklistResponse;

        if (!currentAuditChecklist) {
            auditChecklistResponse = await auditChecklistApiService.addAuditChecklist(fullDto);
        } else {
            fullDto.acId = currentAuditChecklist.acId;
            auditChecklistResponse = await auditChecklistApiService.updateAuditChecklist(fullDto);
        }

        if (auditChecklistResponse.success) {
            snackbar.success(
                currentAuditChecklist
                    ? t('editAuditChecklist.toast.updated', { name: auditChecklistTitle })
                    : t('editAuditChecklist.toast.created', { name: auditChecklistTitle })
            );
            onSave();
            return;
        }

        snackbar.error(t('editAuditChecklist.toast.saveFailed'));
    });

    function getUpdatedAuditChecklist () {
        return {
            acProject: selectedLocation.projectId,
            acIsActive: active,
            acTitle: auditChecklistTitle,
            projectName: selectedLocation.pjName,
            organizationId: selectedOrganization.ogId,
            organizationName: selectedOrganization.ogName,
        };
    }

    async function updateLocationsForOrganization(organizationId) {
        setSelectedLocation(emptyLocation);
        let locationsForOrgResponse = await locationApiService.getLocationsForOrg(organizationId);

        let locationList = [];
        let noSelectedLocation = emptyLocation;
        locationList.push(noSelectedLocation);

        locationList = locationList.concat(locationsForOrgResponse.data.projects);

        if (locationList.length === 2) {
            setSelectedLocation(locationList[1]);
        }

        setLocations(locationList);
    }

    function handleRowOrderChange(params) {
        const { oldIndex, targetIndex } = params;
        const reordered = [...auditChecklistItems];
        const [moved] = reordered.splice(oldIndex, 1);
        reordered.splice(targetIndex, 0, moved);

        reordered.forEach((item, index) => {
            item.ciSortSeq = index + 1;
        });

        setAuditChecklistItems(reordered);
    }

    function handleSubQuestionRowOrderChange(parentId, params) {
        const { oldIndex, targetIndex } = params;
        setAuditChecklistItems(prev => prev.map(item => {
            if (item.ciId !== parentId) return item;

            const reordered = [...(item.childItems || [])];
            const [moved] = reordered.splice(oldIndex, 1);
            reordered.splice(targetIndex, 0, moved);

            reordered.forEach((child, index) => {
                child.ciSortSeq = index + 1;
            });

            return { ...item, childItems: reordered };
        }));
    }

    function handleCellCommit(params) {
        let auditChecklistItem = auditChecklistItems?.find(item => item.ciId === params.row.ciId);

        if (auditChecklistItem) {
            auditChecklistItem[params.field] = params.value;

            if (!auditChecklistItem.newItem) {
                auditChecklistItem.updateItem = true;
            }

            setAuditChecklistItems([...auditChecklistItems]);
        }
    }

    function handleSubQuestionCellCommit(parentId, params) {
        setAuditChecklistItems(prev => prev.map(item => {
            if (item.ciId !== parentId) return item;

            const updatedChildren = (item.childItems || []).map(child => {
                if (child.ciId !== params.row.ciId) return child;
                const updated = { ...child, [params.field]: params.value };
                if (!updated.newItem) {
                    updated.updateItem = true;
                }
                return updated;
            });

            return { ...item, childItems: updatedChildren };
        }));
    }

    function onNewItem() {
        let newAuditChecklistItems = [...auditChecklistItems];

        let newId = 0;
        let newSortSeq = 0;
        newAuditChecklistItems.forEach(item => {
            if (item.ciId > newId) {
                newId = item.ciId;
            }

            if (item.ciSortSeq > newSortSeq) {
                newSortSeq = item.ciSortSeq;
            }

            (item.childItems || []).forEach(child => {
                if (child.ciId > newId) {
                    newId = child.ciId;
                }
            });
        });

        newAuditChecklistItems.push({
            ciId: ++newId,
            ciPrompt: '',
            ciSortSeq: ++newSortSeq,
            ciCategoryId: null,
            ciPriorityId: null,
            ciStatusId: null,
            ciCreateTask: true,
            ciParentItemId: null,
            childItems: [],
            newItem: true,
        });

        setAuditChecklistItems(newAuditChecklistItems);
    }

    function onNewSubQuestion(parentItem) {
        setAuditChecklistItems(prev => prev.map(item => {
            if (item.ciId !== parentItem.ciId) return item;

            const children = item.childItems || [];
            let maxId = item.ciId;
            let maxSort = 0;

            prev.forEach(i => {
                if (i.ciId > maxId) maxId = i.ciId;
                (i.childItems || []).forEach(c => {
                    if (c.ciId > maxId) maxId = c.ciId;
                });
            });

            children.forEach(c => {
                if (c.ciSortSeq > maxSort) maxSort = c.ciSortSeq;
            });

            return {
                ...item,
                childItems: [...children, {
                    ciId: maxId + 1,
                    ciPrompt: '',
                    ciSortSeq: maxSort + 1,
                    ciAuditChecklist: item.ciAuditChecklist,
                    ciParentItemId: item.ciId,
                    ciTriggerAnswer: '1',
                    ciCategoryId: null,
                    ciPriorityId: null,
                    ciStatusId: null,
                    ciCreateTask: true,
                    newItem: true,
                }],
            };
        }));
    }

    const getDetailPanelContent = useCallback(({ row }) => {
        const children = row.childItems || [];

        return (
            <Paper
                variant="outlined"
                sx={{
                    m: 1,
                    ml: 4,
                    p: 2,
                    borderLeft: '3px solid #1976d2',
                    backgroundColor: '#fafbff',
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <SubdirectoryArrowRightIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" color="primary">
                            {t('editAuditChecklist.subQuestionsFor', { prompt: row.ciPrompt || t('common.untitled') })}
                        </Typography>
                    </Stack>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => onNewSubQuestion(row)}
                    >
                        {t('editAuditChecklist.button.addSubQuestion')}
                    </Button>
                </Stack>
                {children.length > 0 ? (
                    <Paper variant="outlined" sx={{ borderRadius: 1 }}>
                        <DataGridPro
                            rows={children}
                            columns={subQuestionColumns}
                            autoHeight
                            hideFooter
                            density="compact"
                            getRowId={(r) => r.ciId}
                            rowReordering
                            onRowOrderChange={(params) => handleSubQuestionRowOrderChange(row.ciId, params)}
                            onCellEditCommit={(params) => handleSubQuestionCellCommit(row.ciId, params)}
                            disableSelectionOnClick
                            sx={gridSx}
                        />
                    </Paper>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
                        {t('editAuditChecklist.help.noSubQuestions')}
                    </Typography>
                )}
            </Paper>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auditChecklistItems, subQuestionColumns]);

    const getDetailPanelHeight = useCallback(() => 'auto', []);

    return (
        <Dialog
            sx={{ '& .MuiDialog-paper': { width: '100%', height: '100vh', borderRadius: 2 }}}
            maxWidth="xl"
            fullWidth
            open={open}>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" component="span" fontWeight={600}>
                        {title}
                    </Typography>
                    {currentAuditChecklist && (
                        <Chip
                            label={active ? t('common.active') : t('common.inactive')}
                            size="small"
                            color={active ? 'success' : 'default'}
                            variant={active ? 'filled' : 'outlined'}
                        />
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
                {/* Settings Section */}
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        {t('editAuditChecklist.section.settings')}
                    </Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} md={isSuperAdmin ? 6 : 8}>
                            <TextField
                                label={t('editAuditChecklist.field.title')}
                                fullWidth
                                size="small"
                                value={auditChecklistTitle}
                                onChange={(event) => setAuditChecklistTitle(event.target.value)}
                            />
                        </Grid>
                        {isSuperAdmin && (
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('fieldName.organization')}</InputLabel>
                                    <Select
                                        label={t('fieldName.organization')}
                                        value={selectedOrganization?.ogId}
                                        onChange={(event) => {
                                            setSelectedOrganization(organizations.find(org => org.ogId === event.target.value));
                                            void updateLocationsForOrganization(event.target.value);
                                        }}
                                    >
                                        {organizations.map((org) => (
                                            <MenuItem key={org.ogId} value={org.ogId}>
                                                {org.ogName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        <Grid item xs={12} md={isSuperAdmin ? 3 : 4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('fieldName.location')}</InputLabel>
                                <Select
                                    label={t('fieldName.location')}
                                    value={selectedLocation?.projectId}
                                    onChange={(event) => {
                                        setSelectedLocation(locations.find(location => location.projectId === event.target.value));
                                    }}
                                >
                                    {locations.map((location) => (
                                        <MenuItem key={location.projectId} value={location.projectId}>
                                            {location.projectName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={active}
                                onChange={() => setActive(!active)}
                                color="success"
                            />
                        }
                        label={t('common.active')}
                        sx={{ mt: 1.5 }}
                    />
                </Paper>

                {/* Checklist Items Section */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        {t('editAuditChecklist.section.items')}
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onNewItem}
                    >
                        {t('editAuditChecklist.button.addItem')}
                    </Button>
                </Stack>
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                    <DataGridPro
                        rows={auditChecklistItems}
                        columns={columns}
                        loading={loading}
                        autoHeight
                        density="comfortable"
                        getRowId={(row) => row.ciId}
                        rowReordering
                        onRowOrderChange={handleRowOrderChange}
                        onCellEditCommit={(params) => handleCellCommit(params)}
                        getDetailPanelContent={getDetailPanelContent}
                        getDetailPanelHeight={getDetailPanelHeight}
                        disableSelectionOnClick
                        localeText={{ noRowsLabel: t('editAuditChecklist.help.noItems') }}
                        sx={gridSx}
                    />
                </Paper>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="outlined" onClick={onCancel} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <SaveButton loading={saving} variant="contained" onClick={handleSave}>
                    {t('editAuditChecklist.button.save')}
                </SaveButton>
            </DialogActions>
            <ConfirmDeleteDialogHazard
                keepMounted
                value={currentSelectedAuditChecklistItem}
                open={showDeleteDialog}
                onClose={onDeleteAuditChecklistItem}
                deleteTitle={dialogTitle()}
                deleteLabel={dialogLabel()}
            ></ConfirmDeleteDialogHazard>
        </Dialog>
    );
}
